"use client";

/**
 * LanguageSelect — source language dropdown backed by /public/transcription-languages.
 *
 * When filterLive is true, only shows languages that support live transcription (isLive: true).
 * Falls back to showing all languages if none match the filter.
 */

import { useTranscriptionLanguages } from "@/features/profile/queries";
import { useTranslation } from "react-i18next";
import { Combobox } from "@/components/shared/combobox";

interface LanguageSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** When true, only shows languages with isLive: true */
  filterLive?: boolean;
  className?: string;
  "data-testid"?: string;
  "aria-label"?: string;
}

export function LanguageSelect({
  id,
  value,
  onChange,
  filterLive = false,
  className,
  "data-testid": testId,
  "aria-label": ariaLabel,
}: LanguageSelectProps) {
  const { t } = useTranslation();
  const { data: languages = [], isLoading } = useTranscriptionLanguages();

  const filtered = filterLive ? languages.filter((l) => l.isLive) : languages;

  // Use full list as fallback if filter returns nothing
  const options = (filtered.length > 0 ? filtered : languages).map((lang) => ({
    value: lang.code,
    label: lang.name,
  }));

  return (
    <Combobox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      loading={isLoading}
      className={className}
      data-testid={testId ?? "language-select"}
      aria-label={ariaLabel ?? t("RECORD.LANGUAGE", "Source Language")}
      searchPlaceholder={t("ACTION.SEARCH", "Search")}
    />
  );
}
